/**
 * Visit Store - Healthcare Visit Management
 *
 * Manages patient visits, visit history, and visit-related operations.
 * Provides hooks for visit state and actions.
 */

import type { VisitDetail } from "@/models/visits";
import { createAsyncActions, createStore, withLogging } from "../../core";
import type { PatientVisit, Visit, VisitStoreState } from "../../types/healthcare";

// Initial state
const initialState: Omit<VisitStoreState, keyof VisitStoreActions> = {
  currentVisit: null,
  visits: [],
  selectedVisitId: null,
  // Patient visit record related state
  patientVisits: [],
  patientVisitsLoading: false,
  patientVisitsError: null,

  // Invoice visit related state
  invoiceVisit: [],
  invoiceVisitLoading: false,
  invoiceVisitError: null,
  loading: false,
  error: null,
  _initialized: false,
  _lastUpdated: Date.now(),
};

// Actions interface
interface VisitStoreActions {
  setCurrentVisit: (visit: VisitDetail | null) => void;
  setVisits: (visits: Visit[]) => void;
  setSelectedVisitId: (id: string | null) => void;
  addVisit: (visit: Visit) => void;
  updateVisit: (id: string, updates: Partial<Visit>) => void;
  removeVisit: (id: string) => void;
  clearVisits: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  reset: () => void;
  // Patient visit record related actions
  setPatientVisits: (visits: PatientVisit[]) => void;
  setPatientVisitsLoading: (loading: boolean) => void;
  setPatientVisitsError: (error: string | null) => void;
  clearPatientVisits: () => void;
  addPatientVisit: (visit: PatientVisit) => void;
  refreshPatientVisits: () => void;

  // Invoice visit related actions
  setInvoiceVisit: (visit: PatientVisit[]) => void;
  setInvoiceVisitLoading: (loading: boolean) => void;
  setInvoiceVisitError: (error: string | null) => void;
  clearInvoiceVisit: () => void;
}

// Complete store state
type CompleteVisitStoreState = VisitStoreState & VisitStoreActions;

// Create the store
const useVisitStore = createStore<CompleteVisitStoreState>(
  withLogging("VisitStore", (set, _get) => ({
    ...initialState,
    ...createAsyncActions<CompleteVisitStoreState>(),

    // Visit management actions
    setCurrentVisit: (visit: VisitDetail | null) =>
      set(() => ({
        currentVisit: visit,
        selectedVisitId: visit?.id || null,
        _lastUpdated: Date.now(),
      })),

    setVisits: (visits: Visit[]) =>
      set(() => ({
        visits,
        _lastUpdated: Date.now(),
      })),

    setSelectedVisitId: (id: string | null) =>
      set((state) => {
        const visit = state.visits.find((v: Visit) => v.id === id) || null;
        return {
          selectedVisitId: id,
          currentVisit: visit,
          _lastUpdated: Date.now(),
        };
      }),

    addVisit: (visit: Visit) =>
      set((state) => ({
        visits: [...state.visits, visit],
        _lastUpdated: Date.now(),
      })),

    updateVisit: (id: string, updates: Partial<Visit>) =>
      set((state) => ({
        visits: state.visits.map((v: Visit) => (v.id === id ? { ...v, ...updates } : v)),
        currentVisit:
          state.currentVisit?.id === id
            ? { ...state.currentVisit, ...updates }
            : state.currentVisit,
        _lastUpdated: Date.now(),
      })),

    removeVisit: (id: string) =>
      set((state) => ({
        visits: state.visits.filter((v: Visit) => v.id !== id),
        currentVisit: state.currentVisit?.id === id ? null : state.currentVisit,
        selectedVisitId: state.selectedVisitId === id ? null : state.selectedVisitId,
        _lastUpdated: Date.now(),
      })),

    clearVisits: () =>
      set(() => ({
        visits: [],
        currentVisit: null,
        selectedVisitId: null,
        _lastUpdated: Date.now(),
      })),

    reset: () =>
      set(() => ({
        ...initialState,
        _lastUpdated: Date.now(),
      })),

    // Patient visit record related actions
    setPatientVisits: (visits: PatientVisit[]) =>
      set(() => ({
        patientVisits: visits,
        patientVisitsError: null,
        _lastUpdated: Date.now(),
      })),

    setInvoiceVisit: (visit: PatientVisit[]) =>
      set(() => ({
        invoiceVisit: visit,
        invoiceVisitError: null,
        _lastUpdated: Date.now(),
      })),

    setInvoiceVisitLoading: (loading: boolean) =>
      set(() => ({
        invoiceVisitLoading: loading,
        _lastUpdated: Date.now(),
      })),

    setInvoiceVisitError: (error: string | null) =>
      set(() => ({
        invoiceVisitError: error,
        invoiceVisitLoading: false,
        _lastUpdated: Date.now(),
      })),

    clearInvoiceVisit: () =>
      set(() => ({
        invoiceVisit: [],
        invoiceVisitError: null,
        invoiceVisitLoading: false,
        _lastUpdated: Date.now(),
      })),

    setPatientVisitsLoading: (loading: boolean) =>
      set(() => ({
        patientVisitsLoading: loading,
        _lastUpdated: Date.now(),
      })),

    setPatientVisitsError: (error: string | null) =>
      set(() => ({
        patientVisitsError: error,
        patientVisitsLoading: false,
        _lastUpdated: Date.now(),
      })),

    clearPatientVisits: () =>
      set(() => ({
        patientVisits: [],
        patientVisitsError: null,
        patientVisitsLoading: false,
        _lastUpdated: Date.now(),
      })),

    addPatientVisit: (visit: PatientVisit) =>
      set((state) => ({
        patientVisits: [visit, ...state.patientVisits],
        _lastUpdated: Date.now(),
      })),

    refreshPatientVisits: () =>
      set((_state) => ({
        _lastUpdated: Date.now(),
        // Refresh logic can be added here, or handled by components themselves
        patientVisitsError: null,
      })),
  })),
  {
    name: "visit-storage", // Match old store name to reuse persisted data
    persist: false,
    version: 1,
    partialize: (state) => ({
      currentVisit: state.currentVisit,
      selectedVisitId: state.selectedVisitId,
    }),
    migrate: (persistedState: unknown, version: number) => {
      let state = persistedState as Partial<VisitStoreState>;

      if (version < 1) {
        const oldState = persistedState as { visitInfo?: Visit };
        if (oldState.visitInfo) {
          state = { currentVisit: oldState.visitInfo, selectedVisitId: oldState.visitInfo.id };
        }
      }

      // Combine the migrated state with the initial state to ensure all fields are present
      // and cast to the full store type to satisfy the type checker.
      // The actions will be added by the store creator function itself.
      return { ...initialState, ...state } as CompleteVisitStoreState;
    },
  },
);

// ===== SELECTORS =====

// Data selectors
export const useVisit = () => useVisitStore((state) => state);
export const useCurrentVisit = () => useVisitStore((state) => state.currentVisit);
export const useVisits = () => useVisitStore((state) => state.visits);
export const useSelectedVisitId = () => useVisitStore((state) => state.selectedVisitId);

// Patient visit record related selectors
export const usePatientVisits = () => useVisitStore((state) => state.patientVisits);
export const usePatientVisitsLoading = () => useVisitStore((state) => state.patientVisitsLoading);
export const usePatientVisitsError = () => useVisitStore((state) => state.patientVisitsError);

// Invoice visit
export const useInvoiceVisit = () => useVisitStore((state) => state.invoiceVisit);
export const useInvoiceVisitLoading = () => useVisitStore((state) => state.invoiceVisitLoading);
export const useInvoiceVisitError = () => useVisitStore((state) => state.invoiceVisitError);

// State selectors
export const useVisitLoading = () => useVisitStore((state) => state.loading);
export const useVisitError = () => useVisitStore((state) => state.error);

// Computed selectors
export const useHasActiveVisit = () => useVisitStore((state) => !!state.currentVisit);
export const useVisitCount = () => useVisitStore((state) => state.visits.length);

// Find visit by ID
export const useVisitById = (id: string | null) =>
  useVisitStore((state) => (id ? state.visits.find((v) => v.id === id) || null : null));

// Individual action selectors to prevent infinite loops
export const useSetCurrentVisit = () => useVisitStore((state) => state.setCurrentVisit);
export const useSetVisits = () => useVisitStore((state) => state.setVisits);
export const useSetSelectedVisitId = () => useVisitStore((state) => state.setSelectedVisitId);
export const useAddVisit = () => useVisitStore((state) => state.addVisit);
export const useUpdateVisit = () => useVisitStore((state) => state.updateVisit);
export const useRemoveVisit = () => useVisitStore((state) => state.removeVisit);
export const useClearVisits = () => useVisitStore((state) => state.clearVisits);
export const useSetVisitLoading = () => useVisitStore((state) => state.setLoading);
export const useSetVisitError = () => useVisitStore((state) => state.setError);
export const useClearVisitError = () => useVisitStore((state) => state.clearError);
export const useResetVisitStore = () => useVisitStore((state) => state.reset);

// Patient visit record related action selectors
export const useSetPatientVisits = () => useVisitStore((state) => state.setPatientVisits);
export const useSetPatientVisitsLoading = () =>
  useVisitStore((state) => state.setPatientVisitsLoading);
export const useSetPatientVisitsError = () => useVisitStore((state) => state.setPatientVisitsError);
export const useClearPatientVisits = () => useVisitStore((state) => state.clearPatientVisits);
export const useAddPatientVisit = () => useVisitStore((state) => state.addPatientVisit);
export const useRefreshPatientVisits = () => useVisitStore((state) => state.refreshPatientVisits);

// Invoice visit related action selectors
export const useSetInvoiceVisit = () => useVisitStore((state) => state.setInvoiceVisit);
export const useSetInvoiceVisitLoading = () =>
  useVisitStore((state) => state.setInvoiceVisitLoading);
export const useSetInvoiceVisitError = () => useVisitStore((state) => state.setInvoiceVisitError);
export const useClearInvoiceVisit = () => useVisitStore((state) => state.clearInvoiceVisit);

// Legacy combined actions hook - use individual hooks above for better performance
export const useVisitActions = () => ({
  setCurrentVisit: useSetCurrentVisit(),
  setVisits: useSetVisits(),
  setSelectedVisitId: useSetSelectedVisitId(),
  addVisit: useAddVisit(),
  updateVisit: useUpdateVisit(),
  removeVisit: useRemoveVisit(),
  clearVisits: useClearVisits(),
  setLoading: useSetVisitLoading(),
  setError: useSetVisitError(),
  clearError: useClearVisitError(),
  reset: useResetVisitStore(),
  // Patient visit record related actions
  setPatientVisits: useSetPatientVisits(),
  setPatientVisitsLoading: useSetPatientVisitsLoading(),
  setPatientVisitsError: useSetPatientVisitsError(),
  clearPatientVisits: useClearPatientVisits(),
  addPatientVisit: useAddPatientVisit(),
  refreshPatientVisits: useRefreshPatientVisits(),
});

// Export the store for direct access if needed
export { useVisitStore };
